import { createContext, useContext, useState, useCallback, type ReactNode } from 'react'

const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'
const TOKEN_KEY = 'km_admin_token'

interface AdminUserInfo {
  email: string
  role: string
}

interface AdminAuthContextValue {
  adminUser: AdminUserInfo | null
  isAdminAuthenticated: boolean
  login: (email: string, password: string) => Promise<string | null>
  logout: () => void
}

const AdminAuthContext = createContext<AdminAuthContextValue | null>(null)

function _parseToken(token: string): AdminUserInfo | null {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]))
    if (!payload.sub || !payload.role) return null
    return { email: payload.sub, role: payload.role }
  } catch {
    return null
  }
}

function _loadStoredUser(): AdminUserInfo | null {
  const token = localStorage.getItem(TOKEN_KEY)
  if (!token) return null
  return _parseToken(token)
}

export function AdminAuthProvider({ children }: { children: ReactNode }) {
  const [adminUser, setAdminUser] = useState<AdminUserInfo | null>(_loadStoredUser)

  const login = useCallback(async (email: string, password: string): Promise<string | null> => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/admin/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        return body?.detail ?? 'Credenziali non valide.'
      }
      const data = await res.json()
      localStorage.setItem(TOKEN_KEY, data.access_token)
      const user = _parseToken(data.access_token)
      setAdminUser(user)
      return null
    } catch {
      return 'Errore di rete. Riprovare.'
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    setAdminUser(null)
  }, [])

  return (
    <AdminAuthContext.Provider value={{ adminUser, isAdminAuthenticated: adminUser !== null, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth(): AdminAuthContextValue {
  const ctx = useContext(AdminAuthContext)
  if (!ctx) throw new Error('useAdminAuth deve essere usato dentro AdminAuthProvider')
  return ctx
}
